/**
 * Calmy scroll motion.
 *
 * Content enters once with a restrained fade-and-rise: 16px, 500ms, staggered
 * 70ms for grouped items. Never repeats.
 *
 * Two deliberate safety properties:
 *
 *  1. The hiding CSS is gated behind [data-calmy-motion-ready] on <html>, which
 *     this file sets. If the script fails to parse or never runs, nothing is
 *     ever hidden — a broken animation must not be able to blank the page.
 *  2. Anything already in the viewport on load is revealed immediately with no
 *     transition, so above-the-fold content never fades in on arrival. That
 *     would delay the largest paint and read as a slow page.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (REDUCED.matches) return;

  const STAGGER = 80;
  const MAX_STAGGER = 4; // beyond this a stagger reads as a queue, not a group

  /**
   * What gets revealed. Deliberately coarse: whole blocks of a section, not
   * every element. Motion is seasoning.
   */
  // Tuned to this build's class names. Deliberately coarse: section-level
  // blocks and card grids, not every element. Motion is seasoning.
  const GROUP_SELECTORS = [
    '.ccard',
    '.cben__i',
    '.crev__v',
    '.ccol__grid > *',
    '.cfaq__list > *',
  ];

  /**
   * Alternating content blocks — image one side, copy the other, reversed on
   * the next row. The image leads and the copy follows one STAGGER behind, so
   * the pair arrives as a sequence rather than as one slab.
   *
   * Ordered pairs rather than a group selector: grouping by parent would
   * stagger by DOM order, and these sections reverse every other row, so on
   * the reversed rows the copy comes first in the markup and would have led.
   * Naming the media and the copy explicitly means the image leads whichever
   * side it is on.
   */
  const PAIR_SELECTORS = [
    ['.ced__m', '.ced__b'],
    ['.cedit__m', '.cedit__p'],
    ['.chiw__media', '.chiw__body'],
    ['.cpil__media', '.cpil__copy'],
  ];

  const BLOCK_SELECTORS = [
    '.csec__head',
    '.csec > *',
    '.cstory__h',
    '.cstory__p',
  ];

  const tag = (el, delay) => {
    if (el.dataset.calmyReveal !== undefined) return;
    // The hero is the first thing on screen and must never fade in — that
    // would delay the largest paint and read as a slow page.
    //
    // BOTH hero classes. The build's editorial hero is .chero; the rotator
    // recovered from the port uses .calmy-hero. Listing only .chero meant the
    // rotator's headline WAS being reveal-animated, and it is the LCP element:
    // measured 10.3s as the sole candidate, which Lighthouse reported as a
    // 21-29s LCP. Nothing had got heavier - total image weight was 78KB.
    if (el.closest('.chero') || el.closest('.calmy-hero')) return;
    // The footer is below the fold by definition and its columns are
    // <details> that JS collapses on mobile. Revealing them added nothing and
    // left the whole link grid blank until the safety net fired — a visibly
    // empty footer is a far worse trade than a missing fade.
    // The footer is below the fold by definition and its columns are
    // <details> that collapse on mobile; revealing them risked a blank grid.
    if (el.closest('.cfoot')) return;
    // Never hide something that has no box right now — an element in an
    // inactive UI state (a hidden grid view, a closed panel) would never
    // intersect, so it would stay invisible for good once revealed by other
    // means.
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    el.setAttribute('data-calmy-reveal', '');
    if (delay) el.style.setProperty('--calmy-reveal-delay', `${delay}ms`);
  };

  const collect = () => {
    BLOCK_SELECTORS.forEach((sel) => document.querySelectorAll(sel).forEach((el) => tag(el, 0)));

    // Pairs first, so the explicit image-then-copy order wins over anything a
    // group selector would otherwise assign to the same elements.
    PAIR_SELECTORS.forEach(([mediaSel, copySel]) => {
      document.querySelectorAll(mediaSel).forEach((media) => tag(media, 0));
      document.querySelectorAll(copySel).forEach((copy) => tag(copy, STAGGER));
    });

    GROUP_SELECTORS.forEach((sel) => {
      const items = [...document.querySelectorAll(sel)];
      // Group by parent so each row/grid staggers from its own start.
      const byParent = new Map();
      items.forEach((el) => {
        const list = byParent.get(el.parentElement) || [];
        list.push(el);
        byParent.set(el.parentElement, list);
      });
      byParent.forEach((list) => {
        list.forEach((el, i) => tag(el, Math.min(i, MAX_STAGGER) * STAGGER));
      });
    });
  };

  const reveal = (el, instant) => {
    if (instant) {
      const prev = el.style.transition;
      el.style.transition = 'none';
      el.classList.add('is-revealed');
      void el.offsetWidth;
      el.style.transition = prev;
    } else {
      el.classList.add('is-revealed');
    }
  };

  const start = () => {
    document.documentElement.setAttribute('data-calmy-motion-ready', '');
    collect();

    const targets = [...document.querySelectorAll('[data-calmy-reveal]')];
    if (!targets.length) return;

    // Anything already on screen appears immediately, untransitioned.
    const fold = window.innerHeight;
    const deferred = [];
    targets.forEach((el) => {
      const top = el.getBoundingClientRect().top;
      if (top < fold * 0.9) reveal(el, true);
      else deferred.push(el);
    });

    if (!('IntersectionObserver' in window)) {
      deferred.forEach((el) => reveal(el, true));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target, false);
          io.unobserve(entry.target); // once per element, never again
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );

    deferred.forEach((el) => io.observe(el));

    // Safety net. Motion is decoration; invisible content is a defect. If
    // anything is still hidden after this, reveal it regardless of whether it
    // was ever scrolled past. An audit run caught five cards in this state on
    // the collection page during lazy pagination. Kept short: this is a
    // correctness backstop, not part of the effect.
    window.setTimeout(() => {
      document.querySelectorAll('[data-calmy-reveal]:not(.is-revealed)').forEach((el) => {
        io.unobserve(el);
        reveal(el, true);
      });
    }, 2500);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Theme Editor swaps section markup without a page load.
  document.addEventListener('shopify:section:load', () => {
    collect();
    document.querySelectorAll('[data-calmy-reveal]:not(.is-revealed)').forEach((el) => reveal(el, true));
  });
})();
